<template>
  <div>
    <v-card>
      <v-card-title>
        Questionnaires
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" offset-md="8">
            <v-text-field v-model="dtSearchQuery" :append-icon="icons.mdiMagnify" label="Search" single-line hide-details dense outlined />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="dtHeaders"
        :items="questionnairesWithAcademyNames"
        :search="dtSearchQuery"
        :loading="isTableLoading"
        :items-per-page="30"
        :footer-props="{ 'items-per-page-options': [15, 30, 45] }"
        item-key="id"
      >
        <template v-slot:item.type="{ item }">
          <v-chip>{{ item.type }}</v-chip>
        </template>

        <template v-slot:item.category="{ item }">
          <v-chip>{{ item.category }}</v-chip>
        </template>

        <template v-slot:item.academyName="{ item }">
          <span>{{ item.academyName }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon class="mr-2" @click.stop="onUpload(item)">
            {{ icons.mdiUploadOutline }}
          </v-icon>
          <v-icon class="mr-2" @click.stop="onDownload(item)">
            {{ icons.mdiDownloadOutline }}
          </v-icon>
          <v-icon @click.stop="onDelete(item)">
            {{ icons.mdiDeleteOutline }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <delete-dialog
      v-model="isDeleteDialogVisible"
      :item="questionnaire"
      :is-loading="isLoading"
      @cancel="onDeleteCanceled"
      @delete="onDeleteConfirmed"
    />
  </div>
</template>

<script>
import {
  mdiDeleteOutline, mdiDownloadOutline, mdiMagnify, mdiUploadOutline,
} from '@mdi/js'
import { computed, onMounted, ref } from 'vue'
import DeleteDialog from '@/components/DeleteDialog.vue'
import useQuestionnaires from '@/composables/api/questionnaires'
import useAcademies from '@/composables/api/academies'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'

export default {
  components: { DeleteDialog },
  setup() {
    const {
      dtHeaders, dtSearchQuery, questionnaires, isLoading, fetchQuestionnaires, deleteQuestionnaire,
    } = useQuestionnaires()
    const { academies, isLoading: areAcademiesLoading, fetchAcademies } = useAcademies()
    const { showSnackbar } = useSnackbar()
    const questionnaire = ref({})
    const isDeleteDialogVisible = ref(false)

    const getAcademyId = academy => academy.id || academy._id || academy.companyId || academy.companyID

    const getQuestionnaireCompanyIds = item => {
      const rawValue = item.companyId
        || item.companyID
        || item.company_id
        || item.academyId
        || item.academyID
        || item.company?.id
        || item.company?._id
        || item.academy?.id
        || item.academy?._id

      if (Array.isArray(rawValue)) return rawValue.filter(Boolean)

      return rawValue ? [rawValue] : []
    }

    const getQuestionnaireAcademyName = item => item.academyName
      || item.companyName
      || item.company?.name
      || item.academy?.name

    const academyNameById = computed(() => academies.value.reduce((result, academy) => {
      const academyId = getAcademyId(academy)
      if (academyId) result[academyId] = academy.name

      return result
    }, {}))

    const questionnairesWithAcademyNames = computed(() => questionnaires.value.map(item => ({
      ...item,
      academyName: getQuestionnaireAcademyName(item)
        || getQuestionnaireCompanyIds(item).map(companyId => academyNameById.value[companyId] || companyId).join(', ')
        || 'Global',
    })))

    const isTableLoading = computed(() => isLoading.value || areAcademiesLoading.value)

    onMounted(async () => {
      await Promise.all([
        fetchQuestionnaires(),
        fetchAcademies(),
      ])
    })

    const showPendingAction = action => {
      showSnackbar(`${action} endpoint will be connected later`, SnackbarType.INFO)
    }

    const onUpload = () => showPendingAction('Upload')
    const onDownload = () => showPendingAction('Download')
    const onDelete = item => {
      questionnaire.value = item
      isDeleteDialogVisible.value = true
    }
    const onDeleteCanceled = () => {
      questionnaire.value = {}
      isDeleteDialogVisible.value = false
    }
    const onDeleteConfirmed = async item => {
      await deleteQuestionnaire(item)
      await fetchQuestionnaires()
      isDeleteDialogVisible.value = false
      questionnaire.value = {}
    }

    return {
      dtHeaders,
      dtSearchQuery,
      questionnairesWithAcademyNames,
      isTableLoading,
      isLoading,
      questionnaire,
      isDeleteDialogVisible,
      onUpload,
      onDownload,
      onDelete,
      onDeleteCanceled,
      onDeleteConfirmed,
      icons: {
        mdiDeleteOutline,
        mdiDownloadOutline,
        mdiMagnify,
        mdiUploadOutline,
      },
    }
  },
}
</script>
